import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import {
  Loader2, ArrowLeft, Camera, ShoppingCart, Trash2, CreditCard,
  Globe, X, Package, Dog,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getCartId() {
  let id = localStorage.getItem('sport_cart_id');
  if (!id) {
    id = 'cart_' + Math.random().toString(36).slice(2, 14);
    localStorage.setItem('sport_cart_id', id);
  }
  return id;
}

export default function SportCartPage() {
  const { language, setLanguage } = useLanguage();
  const cartId = getCartId();
  const [cart, setCart] = useState({ items: [], total: 0, items_count: 0 });
  const [loading, setLoading] = useState(true);
  const [checkoutMode, setCheckoutMode] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');

  const t = language === 'pl' ? {
    title: 'Koszyk', empty: 'Koszyk jest pusty', back: 'Wrocdo galerii',
    total: 'Suma', checkout: 'Zamow', clearCart: 'Wyczysc koszyk',
    remove: 'Usun', items: 'szt.', dogPack: 'Pakiet',
    buyerInfo: 'Dane kupujacego', name: 'Imie i nazwisko', email: 'Email',
    phone: 'Telefon', pay: 'Przejdz do platnosci', ordering: 'Przetwarzanie...',
    backToCart: 'Powrot do koszyka', browseMore: 'Przegladaj dalej',
  } : {
    title: 'Cart', empty: 'Your cart is empty', back: 'Back to gallery',
    total: 'Total', checkout: 'Checkout', clearCart: 'Clear cart',
    remove: 'Remove', items: 'items', dogPack: 'Pack',
    buyerInfo: 'Buyer information', name: 'Full name', email: 'Email',
    phone: 'Phone', pay: 'Proceed to payment', ordering: 'Processing...',
    backToCart: 'Back to cart', browseMore: 'Browse more',
  };

  const refreshCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sport/shop/cart/${cartId}`);
      setCart(res.data);
    } catch (err) {
      console.error('Cart fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [cartId]);

  useEffect(() => { refreshCart(); }, [refreshCart]);

  const handleRemove = async (photoId) => {
    try {
      await axios.delete(`${API}/sport/shop/cart/${cartId}/item/${photoId}`);
      await refreshCart();
    } catch (err) {
      console.error('Remove error:', err);
    }
  };

  const handleClear = async () => {
    try {
      await axios.delete(`${API}/sport/shop/cart/${cartId}`);
      await refreshCart();
    } catch (err) {
      console.error('Clear error:', err);
    }
  };

  const handleCheckout = async () => {
    if (!buyerName || !buyerEmail) return;
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/sport/shop/cart/${cartId}/checkout`, {
        buyer_name: buyerName, buyer_email: buyerEmail, buyer_phone: buyerPhone,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => window.history.back()} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <ShoppingCart className="w-6 h-6" />
            <h1 className="text-lg font-bold">{t.title}</h1>
            <span className="text-sm text-muted-foreground">({cart.items_count} {t.items})</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}>
            <Globe className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {cart.items.length === 0 ? (
          /* Empty cart */
          <div className="text-center py-20">
            <ShoppingCart className="w-20 h-20 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg text-muted-foreground mb-6">{t.empty}</p>
            <Link to="/sport">
              <Button>{t.browseMore}</Button>
            </Link>
          </div>
        ) : checkoutMode ? (
          /* Checkout form */
          <div className="max-w-md mx-auto space-y-6">
            <h2 className="text-xl font-bold">{t.buyerInfo}</h2>
            <div>
              <label className="text-sm text-muted-foreground">{t.name} *</label>
              <input
                type="text" value={buyerName} onChange={e => setBuyerName(e.target.value)}
                className="w-full mt-1 px-4 py-3 bg-secondary border border-border rounded-lg text-sm"
                placeholder="Jan Kowalski"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">{t.email} *</label>
              <input
                type="email" value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)}
                className="w-full mt-1 px-4 py-3 bg-secondary border border-border rounded-lg text-sm"
                placeholder="jan@example.com"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">{t.phone}</label>
              <input
                type="tel" value={buyerPhone} onChange={e => setBuyerPhone(e.target.value)}
                className="w-full mt-1 px-4 py-3 bg-secondary border border-border rounded-lg text-sm"
                placeholder="+48 123 456 789"
              />
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium">{t.total}</span>
                <span className="text-2xl font-bold">{cart.total} {cart.currency}</span>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setCheckoutMode(false)}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> {t.backToCart}
                </Button>
                <Button
                  className="flex-1"
                  disabled={!buyerName || !buyerEmail || checkoutLoading}
                  onClick={handleCheckout}
                >
                  {checkoutLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.ordering}</>
                  ) : (
                    <><CreditCard className="w-4 h-4 mr-2" /> {t.pay}</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* Cart items gallery */
          <div>
            {/* Grid of cart items */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
              {cart.items.map((item, idx) => (
                <motion.div
                  key={item.photo_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className="bg-secondary rounded-lg overflow-hidden group relative"
                >
                  {/* Thumbnail */}
                  <div className="aspect-square relative overflow-hidden">
                    {item.thumbnail ? (
                      <img
                        src={item.thumbnail}
                        alt={item.filename || ''}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-secondary">
                        <Camera className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}

                    {/* Dog pack badge */}
                    {item.type === 'dog_pack' && (
                      <div className="absolute top-2 left-2 bg-green-500/80 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                        <Package className="w-3 h-3" /> {t.dogPack}
                      </div>
                    )}

                    {/* Remove button */}
                    <button
                      onClick={() => handleRemove(item.photo_id)}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white/70 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Info */}
                  <div className="px-2 py-2">
                    <p className="text-[11px] font-mono text-muted-foreground truncate">
                      {item.filename || item.photo_id.slice(0, 12)}
                    </p>
                    <p className="text-sm font-semibold mt-0.5">
                      {item.price} PLN
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t border-border pt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={handleClear}>
                    <Trash2 className="w-4 h-4 mr-1" /> {t.clearCart}
                  </Button>
                  <Link to="/sport">
                    <Button variant="ghost" size="sm">{t.browseMore}</Button>
                  </Link>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">{t.total}</p>
                    <p className="text-2xl font-bold">{cart.total} {cart.currency}</p>
                  </div>
                  <Button size="lg" onClick={() => setCheckoutMode(true)}>
                    <CreditCard className="w-5 h-5 mr-2" /> {t.checkout}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
